"use client";

import React, { useEffect, useState, useRef } from "react";
import { motion, useInView } from "framer-motion";

// --- Count Up Hook ---
const useCountUp = (target: number, duration: number, active: boolean) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!active) {
      setCount(0);
      return;
    }

    let start = 0;
    const end = target;
    const totalFrames = Math.round(duration * 60);
    let frame = 0;

    const timer = setInterval(() => {
      frame++;
      const progress = frame / totalFrames;
      // Easing function (easeOutQuad)
      const easedProgress = 1 - (1 - progress) * (1 - progress);
      const current = Math.round(end * easedProgress);

      if (frame >= totalFrames) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(current);
      }
    }, 1000 / 60);

    return () => clearInterval(timer);
  }, [target, duration, active]);

  return count;
};

const stats = [
  { value: 24, label: "Industries Served", suffix: "+" },
  { value: 1000, label: "Enterprise Clients", suffix: "+" },
  { value: 30, label: "Years of Excellence", suffix: "+" },
  { value: 20, label: "States Covered", suffix: "+" }
];

export function IndustriesStatsBar() {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, amount: 0.5 });

  return (
    <div className="relative z-30 w-full bg-white border-t-2 border-transparent bg-clip-padding">
      {/* Top Border Gradient */}
      <div className="absolute top-0 left-0 w-full h-[3px] bg-gradient-to-r from-[#2C78B9] via-[#059A8F] to-[#2C78B9]" />

      <div className="container-site" ref={containerRef}>
        <div className="max-w-[1000px] mx-auto py-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-0">
            {stats.map((stat, idx) => (
              <StatItem
                key={idx}
                stat={stat}
                active={isInView}
                index={idx}
                isLast={idx === stats.length - 1}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatItem({ stat, active, index, isLast }: { stat: any; active: boolean; index: number; isLast: boolean }) {
  const count = useCountUp(stat.value, 2, active);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={active ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className={`flex flex-col items-center text-center px-4 ${!isLast ? 'md:border-r border-slate-100' : ''}`}
    >
      <div className="text-4xl lg:text-5xl font-black text-[#2C78B9] tracking-tighter mb-2">
        {count}{stat.suffix}
      </div>
      <div className="text-[11px] lg:text-[13px] text-slate-500 font-bold uppercase tracking-[0.1em]">
        {stat.label}
      </div>
    </motion.div>
  );
}
